import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import { Box, Container, css } from '@coursera/coursera-ui';

import type { Customization } from 'bundles/enterprise-admin-skills/components/types';
import {
  ConflictingNamesNotification,
  SuccessfullyResolvedErrorsNotification,
} from 'bundles/enterprise-skillset-bank/components/Notifications';
import { Empty } from 'bundles/enterprise-skillset-bank/components/SkillSetReview';
import styles from 'bundles/enterprise-skillset-bank/components/styles';
import { Button, Heading, Spacer } from 'bundles/enterprise-ui/components';
import type { SkillSet } from 'bundles/naptimejs/resources/skillSets.v1';

import _t from 'i18n!nls/enterprise-admin-skills';

type PropsFromCaller = {
  selectedSkillSets: SkillSet[];
  skillSetsRequiringAttention: SkillSet[];
  customizations: Customization[];
  renderSkillSetCard: (skillset: SkillSet, index: number) => JSX.Element;
  onBack: () => void;
  onCancel: () => void;
  onConfirm: () => void;
};

type Props = PropsFromCaller;

type PropsForHeader = {
  showSuccessfulResolutionNotification: boolean;
  showConflictingNamesError: boolean;
};

type PropsForFooter = {
  onBack: () => void;
  onCancel: () => void;
  onConfirm: () => void;
};

const SkillSetConflictResolutionHeader: React.FC<PropsForHeader> = ({
  showSuccessfulResolutionNotification,
  showConflictingNamesError,
}) => (
  <Container isFluid rootClassName={styles.HeaderContainer}>
    <div {...css(styles.ContainerContent)}>
      <Spacer size={32} />
      <Heading as="h1">{_t('SkillSets requiring your attention')}</Heading>
      <Spacer size={12} />
      <Typography2 component="p">
        {_t('Before you can review and add your selected SkillSets, there are some SkillSets requiring your attention')}
      </Typography2>
      {showSuccessfulResolutionNotification && (
        <div data-e2e="successful-resolution-notification">
          <Spacer size={32} />
          <SuccessfullyResolvedErrorsNotification />
          <Spacer size={12} />
        </div>
      )}
      {showConflictingNamesError && (
        <div data-e2e="conflicting-names-notification">
          <Spacer size={32} />
          <ConflictingNamesNotification />
          <Spacer size={12} />
        </div>
      )}
      <Spacer size={24} />
    </div>
  </Container>
);

const SkillSetConflictResolutionFooter: React.FC<PropsForFooter> = ({ onBack, onCancel, onConfirm }) => (
  <Container isFluid rootClassName={styles.FooterContainer}>
    <Spacer size={24} />
    <Box justifyContent="between" alignItems="center" rootClassName={styles.ContainerContent}>
      <Button variant="secondary" onClick={onBack} data-e2e="skillset-conflict-resolution-back">
        {_t('Back')}
      </Button>
      <Box>
        <Button variant="secondary" onClick={onCancel}>
          {_t('Cancel')}
        </Button>
        <Spacer size={12} />
        <Button variant="primary" onClick={onConfirm} data-e2e="continue-to-review">
          {_t('Continue')}
        </Button>
      </Box>
    </Box>
    <Spacer size={24} />
  </Container>
);

const SkillSetConflictResolution: React.FC<Props> = ({
  selectedSkillSets,
  skillSetsRequiringAttention,
  customizations,
  renderSkillSetCard,
  onBack,
  onCancel,
  onConfirm,
}) => {
  const hasBeenEdited = (skillSet: SkillSet) =>
    customizations.some((customization) => customization.id === skillSet.id);
  const hasNothingToAdd = skillSetsRequiringAttention.length === 0 && selectedSkillSets.length === 0;
  const showSuccessfulResolutionNotification = skillSetsRequiringAttention.every(hasBeenEdited) && !hasNothingToAdd;
  const showConflictingNamesError = skillSetsRequiringAttention.length > 0 && !showSuccessfulResolutionNotification;

  return (
    <div data-e2e="skillsets-conflict-resolution">
      <SkillSetConflictResolutionHeader
        showSuccessfulResolutionNotification={showSuccessfulResolutionNotification}
        showConflictingNamesError={showConflictingNamesError}
      />
      <Container isFluid rootClassName={styles.ListContainer}>
        <Spacer size={24} />
        <div {...css(styles.ContainerContent)}>
          {hasNothingToAdd && (
            <div>
              <Spacer size={64} />
              <Empty onBack={onBack} />
            </div>
          )}
          {!hasNothingToAdd && (
            <div>
              <Spacer size={24} />
              <Box flexWrap="wrap" alignItems="stretch" justifyContent="between">
                {skillSetsRequiringAttention.map((skillSet, index) => skillSet && renderSkillSetCard(skillSet, index))}
              </Box>
            </div>
          )}
        </div>
        <Spacer size={32} />
      </Container>
      <SkillSetConflictResolutionFooter onBack={onBack} onCancel={onCancel} onConfirm={onConfirm} />
    </div>
  );
};

export default SkillSetConflictResolution;
